// Flow resolution (PRD section 17, "Simulación por flujos"): each tick, every line of the network
// is solved in rates per minute and the stores at its ends are updated by rate × tick.
//
// A line is a tree that pours into one store. Two sweeps solve it exactly:
// 1. Upstream to downstream, each node works out what it could deliver (its potential): sources offer
//    their stock, belts and routes cap the sum at their rate, machines turn inputs into crafts up to
//    their speed.
// 2. Downstream to upstream, the room in the sink store caps the root, and each node passes the flow it
//    is allowed back to its feeders in proportion to what each could deliver. Nothing is lost:
//    feeders are never asked for more than their potential.
//
// Stores couple the lines. A tick solves every line twice:
// - first in network order (upstream first), so material can cross empty stores within the tick;
// - then in reverse order, with what each node has left of its capacity, so a full store passes on
//   the room its consumers freed this tick. Without this second round a full store would always read
//   one tick's worth short of full.
import type { ItemId } from './data';
import type { FlowNode, Line, Network } from './network';
import { storeTotal, type Store } from './world';

/** Milliseconds of game time per tick: the fixed step of the simulation. */
export const TICK_MS = 100;
/** Minutes of game time per tick, to turn rates per minute into amounts. */
export const TICK_MINUTES = TICK_MS / 60_000;

/** Below this, a rate or an amount counts as zero. */
export const EPSILON = 1e-9;

type Vec = Map<ItemId, number>;

/**
 * Why a piece of the chain runs at the rate it does.
 * - `working`: at its own maximum, with nothing waiting.
 * - `bottleneck`: at its maximum while more material is waiting upstream; this is what limits the chain.
 * - `starved`: below its maximum for lack of material (including stopped for lack of any).
 * - `blocked`: below what it could do because there is no room downstream.
 */
export type FlowStatus = 'working' | 'bottleneck' | 'starved' | 'blocked';

export interface NodeFigures {
  readonly node: FlowNode;
  /** Items per minute leaving the node. */
  readonly out: ReadonlyMap<ItemId, number>;
  /** Items per minute entering the node (machines, belts, routes). */
  readonly in: ReadonlyMap<ItemId, number>;
  /** Crafts per minute, for machines. */
  readonly crafts: number;
  /** Its own limit: crafts per minute for machines, items per minute for the rest; Infinity for sources. */
  readonly max: number;
  readonly status: FlowStatus;
}

export interface StoreFigures {
  /** Items per minute poured into the store and drawn from it during the tick. */
  readonly in: ReadonlyMap<ItemId, number>;
  readonly out: ReadonlyMap<ItemId, number>;
}

export interface TickFigures {
  readonly lines: ReadonlyMap<string, readonly NodeFigures[]>;
  readonly stores: ReadonlyMap<Store, StoreFigures>;
}

const total = (v: ReadonlyMap<ItemId, number>): number => storeTotal(v);

function add(into: Vec, from: ReadonlyMap<ItemId, number>, factor = 1): void {
  for (const [item, n] of from) into.set(item, (into.get(item) ?? 0) + n * factor);
}

function scaled(v: ReadonlyMap<ItemId, number>, factor: number): Vec {
  const out: Vec = new Map();
  for (const [item, n] of v) out.set(item, n * factor);
  return out;
}

function capTotal(v: ReadonlyMap<ItemId, number>, cap: number): Vec {
  const sum = total(v);
  return sum > cap ? scaled(v, Math.max(0, cap) / sum) : new Map(v);
}

/** A node's own limit: crafts per minute for machines, items per minute for everything else. */
function nodeMax(node: FlowNode): number {
  switch (node.kind) {
    case 'source':
      return Infinity;
    case 'belt':
    case 'route':
      return node.capacity;
    case 'machine':
      return node.machine.def.craftsPerMinute;
    case 'extract':
      return node.rate;
  }
}

/** What one round of solving a line did at one node, measured in the node's own unit (see nodeMax). */
interface Round {
  readonly out: Vec;
  readonly in: Vec;
  readonly crafts: number;
  /** Capacity used this round. */
  readonly used: number;
  /** What it could have done this round with what was offered, within its capacity. */
  readonly potential: number;
  /** What was offered to it this round, before its own limit. */
  readonly offered: number;
}

interface Forward {
  /** What arrives from the feeders at their potential. */
  readonly offered: Vec;
  /** What the node could deliver, before the sink is considered. */
  readonly potential: Vec;
  /** The offer in the node's own unit: crafts the inputs allow, or items. */
  readonly offeredUnits: number;
}

type Level = (store: Store) => Vec;

function forward(node: FlowNode, offered: Vec, capacity: number, level: Level): Forward {
  switch (node.kind) {
    case 'source': {
      // A store can hand over its whole stock within the tick.
      const potential = scaled(level(node.store), 1 / TICK_MINUTES);
      return { offered, potential, offeredUnits: total(potential) };
    }
    case 'extract': {
      // The ground offers what is left of the extraction rate this tick.
      const left = Math.max(0, capacity);
      return { offered, potential: new Map([[node.item, left]]), offeredUnits: left };
    }
    case 'belt':
    case 'route':
      return { offered, potential: capTotal(offered, capacity), offeredUnits: total(offered) };
    case 'machine': {
      let crafts = Infinity;
      for (const [item, need] of node.machine.def.recipe.in) crafts = Math.min(crafts, (offered.get(item) ?? 0) / need);
      return { offered, potential: scaled(node.machine.def.recipe.out, Math.max(0, Math.min(crafts, capacity))), offeredUnits: crafts };
    }
  }
}

/** What a node draws from its feeders to deliver `allowed`, and the crafts that takes. */
function intake(node: FlowNode, allowed: ReadonlyMap<ItemId, number>): { draw: Vec; crafts: number } {
  if (node.kind !== 'machine') return { draw: new Map(allowed), crafts: 0 };
  const recipe = node.machine.def.recipe;
  let crafts = Infinity;
  for (const [item, n] of recipe.out) crafts = Math.min(crafts, (allowed.get(item) ?? 0) / n);
  if (!Number.isFinite(crafts)) crafts = 0;
  return { draw: scaled(recipe.in, crafts), crafts };
}

/**
 * Solves one line against the current stock levels and the capacity each node has left this tick,
 * then moves the material: sources lose what they hand over and the sink gains what arrives.
 */
function solveLine(line: Line, level: Level, spent: Map<FlowNode, number>, stores: Map<Store, { in: Vec; out: Vec }>): Round[] {
  const capacity = (node: FlowNode): number => nodeMax(node) - (spent.get(node) ?? 0);
  const passes: Forward[] = [];
  line.nodes.forEach((node, i) => {
    const offered: Vec = new Map();
    for (const c of line.children[i] ?? []) add(offered, passes[c]?.potential ?? new Map());
    passes.push(forward(node, offered, capacity(node), level));
  });

  const root = line.nodes.length - 1;
  const room = Math.max(0, line.sink.capacity - total(level(line.sink))) / TICK_MINUTES;
  const allowed: Vec[] = line.nodes.map(() => new Map<ItemId, number>());
  allowed[root] = capTotal(passes[root]?.potential ?? new Map<ItemId, number>(), room);

  const rounds = new Array<Round>(line.nodes.length);
  for (let i = root; i >= 0; i--) {
    const node = line.nodes[i] as FlowNode;
    const pass = passes[i] as Forward;
    const out = allowed[i] ?? new Map<ItemId, number>();
    const { draw, crafts } = intake(node, out);
    // Share what the node draws among its feeders, item by item, in proportion to their potential.
    for (const c of line.children[i] ?? []) {
      const share: Vec = new Map();
      for (const [item, n] of passes[c]?.potential ?? []) {
        const offered = pass.offered.get(item) ?? 0;
        if (offered > 0) share.set(item, ((draw.get(item) ?? 0) * n) / offered);
      }
      allowed[c] = share;
    }
    if (node.kind === 'source') {
      const v = level(node.store);
      for (const [item, n] of out) {
        const left = (v.get(item) ?? 0) - n * TICK_MINUTES;
        if (left > EPSILON) v.set(item, left);
        else v.delete(item);
      }
      const figures = stores.get(node.store);
      if (figures) add(figures.out, out);
    }
    const isMachine = node.kind === 'machine';
    const used = isMachine ? crafts : total(out);
    spent.set(node, (spent.get(node) ?? 0) + used);
    rounds[i] = {
      out,
      in: isMachine ? draw : node.kind === 'source' || node.kind === 'extract' ? new Map<ItemId, number>() : out,
      crafts,
      used,
      potential: isMachine ? Math.max(0, Math.min(pass.offeredUnits, capacity(node) + used)) : total(pass.potential),
      offered: pass.offeredUnits,
    };
  }

  const into = level(line.sink);
  const arrived = allowed[root] ?? new Map<ItemId, number>();
  add(into, arrived, TICK_MINUTES);
  const sinkFigures = stores.get(line.sink);
  if (sinkFigures) add(sinkFigures.in, arrived);
  return rounds;
}

function statusOf(node: FlowNode, first: Round, second: Round | undefined): FlowStatus {
  const max = nodeMax(node);
  const flow = first.used + (second?.used ?? 0);
  const potential = second ? first.used + second.potential : first.potential;
  const offered = Math.max(first.offered, second ? first.used + second.offered : 0);
  const tolerance = EPSILON * Math.max(1, Number.isFinite(max) ? max : potential);
  if (flow < potential - tolerance) return 'blocked';
  if (node.kind === 'source') return 'working';
  if (flow >= max - tolerance) return offered > max + tolerance ? 'bottleneck' : 'working';
  return 'starved';
}

/**
 * Runs one tick of the network. With `commit`, stock levels are written back to the stores;
 * without, the stores are left untouched and only the figures are returned (to show rates while paused).
 */
export function step(network: Network, allStores: Iterable<Store>, commit: boolean): TickFigures {
  const levels = new Map<Store, Vec>();
  const level: Level = (store) => {
    let v = levels.get(store);
    if (!v) levels.set(store, (v = new Map(store.items)));
    return v;
  };
  const stores = new Map<Store, { in: Vec; out: Vec }>();
  for (const store of allStores) stores.set(store, { in: new Map(), out: new Map() });
  const spent = new Map<FlowNode, number>();
  const firsts = network.lines.map((line) => solveLine(line, level, spent, stores));
  const seconds = new Array<Round[]>(network.lines.length);
  for (let i = network.lines.length - 1; i >= 0; i--) seconds[i] = solveLine(network.lines[i] as Line, level, spent, stores);

  const lines = new Map<string, NodeFigures[]>();
  network.lines.forEach((line, l) => {
    lines.set(
      line.id,
      line.nodes.map((node, i): NodeFigures => {
        const first = firsts[l]?.[i] as Round;
        const second = seconds[l]?.[i];
        const out = new Map(first.out);
        const into = new Map(first.in);
        if (second) {
          add(out, second.out);
          add(into, second.in);
        }
        return { node, out, in: into, crafts: first.crafts + (second?.crafts ?? 0), max: nodeMax(node), status: statusOf(node, first, second) };
      }),
    );
  });

  if (commit) {
    for (const [store, v] of levels) {
      // Rounding can leave a full store a hair over its capacity: trim it so it reads exactly full.
      const sum = total(v);
      const trim = sum > store.capacity ? store.capacity / sum : 1;
      store.items.clear();
      for (const [item, n] of v) if (n * trim > EPSILON) store.items.set(item, n * trim);
    }
  }
  return { lines, stores };
}
